// src/components/DefectiveItemForm.jsx
//
// SHARED FORM — used by both TaskDetail.js (technician view) and
// ServiceDetail.js (manager view) to add a defective item to a service job.
//
// WHY THIS EXISTS:
// Before this component, both views had their own copy of this form's JSX.
// This already caused one real bug (a "Part Name" vs "Brand Name" label
// mismatch went unnoticed in one view after being fixed in the other).
// A single shared definition means any future field/label/grouping change
// only needs to happen in ONE place — both views update automatically.
//
// This component owns ONLY the form markup and the machine-select
// auto-fill behavior. It does NOT own state or the actual save call —
// the parent page owns `defectiveItem` state and passes down its setter,
// and owns the final Firestore write (via the governed addDefectiveItem()
// in serviceTaskService.js — never write defective_items[] directly).
//
// Visual styling (colors, spacing) is intentionally left to each parent
// via style props, since the two views have different surrounding layouts
// — but the FIELD LIST, ORDER, and GROUPING are fixed here, once.
//
// i18n NOTE:
// This form is shared between the technician view (Hinglish/English
// toggle) and the manager view (English-only, by design — see
// SESSION_STATE i18n scope decision). It stays a "dumb" presentational
// component: it does NOT call useLang() itself and does NOT render
// <LangToggle/> (that belongs in the PARENT page's header, since the
// toggle is a page-level control, not a per-form one). Instead it
// accepts a `lang` prop — TaskDetail.js should pass its own
// `useLang().lang` down; ServiceDetail.js should simply omit the prop,
// which defaults to "en" and keeps the manager view unaffected.

import { STRINGS } from "../i18n/technicianStrings";

function translate(key, lang) {
  const entry = STRINGS[key];
  if (!entry) return key; // missing key — fail loud-ish in dev, never crash
  return entry[lang] || entry.en || key;
}

export default function DefectiveItemForm({
  defectiveItem,
  setDefectiveItem,
  siteMachines = [],
  onSave,
  onAddMachine, // NEW — callback, parent decides navigation (has client_id/site_id context)
  lang = "en", // NEW — "en" | "hi", defaults to English (manager view unaffected)
  inputStyle = {},
  containerStyle = {},
  sectionLabelStyle = {},
  saveButtonStyle = {},
  emptyStateStyle = {}, // NEW — style for the empty-state block
  addMachineButtonStyle = {}, // NEW
  saveLabel, // if explicitly passed, overrides the dictionary default (backward compat)
  saveDisabled = false, // NEW — parent sets true while a save is already in flight, blocks double-click → duplicate defective_items entries
}) {
  const t = (key) => translate(key, lang);

  const handleMachineSelect = (e) => {
    const mid = e.target.value;
    const m = siteMachines.find((x) => x.id === mid);
    setDefectiveItem((prev) => ({
      ...prev,
      machine_id: mid,
      machine_description: m ? (m.machine_name || prev.machine_description) : prev.machine_description,
      machine_model: m ? (m.model_no || prev.machine_model) : prev.machine_model,
    }));
  };

  const hasMachines = siteMachines.length > 0;

  return (
    <div style={containerStyle}>
      <p style={sectionLabelStyle}>{t("machine_details_heading")}</p>

      {hasMachines ? (
        <select value={defectiveItem.machine_id || ""} onChange={handleMachineSelect} style={inputStyle}>
          <option value="">{t("select_machine_placeholder")}</option>
          {siteMachines.map((m) => (
            <option key={m.id} value={m.id}>
              {m.machine_name || "Unnamed"} {m.model_no ? `· ${m.model_no}` : ""}
            </option>
          ))}
        </select>
      ) : (
        // EMPTY STATE — replaces the bare empty dropdown. Explains WHY
        // it's empty and gives a clear next action, instead of leaving
        // the technician staring at a dropdown with nothing in it.
        <div style={emptyStateStyle}>
          <p style={{ margin: "0 0 4px 0", fontWeight: 600 }}>
            {t("no_machine_registered_title")}
          </p>
          <p style={{ margin: "0 0 8px 0" }}>{t("no_machine_registered_body")}</p>
          <button
            type="button"
            onClick={() => {
              if (onAddMachine) {
                onAddMachine();
              } else if (process.env.NODE_ENV !== "production") {
                // eslint-disable-next-line no-console
                console.warn(
                  "DefectiveItemForm: onAddMachine prop not provided — " +
                    "'+ Machine Add Karo' button has nowhere to navigate."
                );
              }
            }}
            style={addMachineButtonStyle}
          >
            {t("add_machine_button")}
          </button>
        </div>
      )}

      <input
        placeholder={t("machine_description_placeholder")}
        value={defectiveItem.machine_description}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, machine_description: e.target.value }))}
        style={inputStyle}
      />
      <input
        placeholder={t("machine_model_placeholder")}
        value={defectiveItem.machine_model}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, machine_model: e.target.value }))}
        style={inputStyle}
      />

      <input
        placeholder={t("item_name_placeholder")}
        value={defectiveItem.item_name}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, item_name: e.target.value }))}
        style={inputStyle}
      />
      <input
        type="number"
        placeholder={t("qty_placeholder")}
        value={defectiveItem.qty}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, qty: e.target.value }))}
        style={inputStyle}
      />

      <p style={sectionLabelStyle}>{t("part_details_heading")}</p>
      <input
        placeholder={t("brand_name_placeholder")}
        value={defectiveItem.part_name}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, part_name: e.target.value }))}
        style={inputStyle}
      />
      <input
        placeholder={t("part_code_placeholder")}
        value={defectiveItem.part_code}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, part_code: e.target.value }))}
        style={inputStyle}
      />
      <input
        placeholder={t("part_serial_placeholder")}
        value={defectiveItem.serial_no}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, serial_no: e.target.value }))}
        style={inputStyle}
      />

      <input
        placeholder={t("remarks_placeholder")}
        value={defectiveItem.remarks}
        onChange={(e) => setDefectiveItem((prev) => ({ ...prev, remarks: e.target.value }))}
        style={inputStyle}
      />

      <button onClick={onSave} disabled={saveDisabled} style={saveButtonStyle}>
        {saveLabel || t("save_defective_item")}
      </button>
    </div>
  );
}
